import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * Kapselt das Oeffnen/Schliessen eines Popovers (Notification-Panel,
 * App-Switcher, Profil-Menue): Outside-Click und Escape schliessen, der
 * Trigger toggelt. Liefert Refs + ARIA-freundlichen Zustand, damit alle
 * Shell-Popovers identisch funktionieren. Mit `rovingFocus` kommt die
 * Pfeiltasten-Navigation fuer Menues dazu.
 */
export function useDismissable(initial = false, { rovingFocus = false, itemSelector = '[role="menuitem"]', closeOnTab = true } = {}) {
    const [open, setOpen] = useState(initial);
    const containerRef = useRef(null);
    const wasOpen = useRef(initial);
    const close = useCallback(() => setOpen(false), []);
    const toggle = useCallback(() => setOpen((v) => !v), []);
    useEffect(() => {
        if (!open)
            return;
        const onPointer = (e) => {
            const node = containerRef.current;
            if (node && e.target instanceof Node && !node.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
                return;
            }
            if (!rovingFocus)
                return;
            const node = containerRef.current;
            if (!node)
                return;
            const items = Array.from(node.querySelectorAll(itemSelector)).filter((el) => !el.hasAttribute('disabled') && el.getAttribute('aria-disabled') !== 'true');
            if (items.length === 0)
                return;
            const current = items.findIndex((el) => el === document.activeElement);
            let next = -1;
            switch (e.key) {
                case 'ArrowDown':
                case 'ArrowRight':
                    next = current < 0 ? 0 : (current + 1) % items.length;
                    break;
                case 'ArrowUp':
                case 'ArrowLeft':
                    next = current < 0 ? items.length - 1 : (current - 1 + items.length) % items.length;
                    break;
                case 'Home':
                    next = 0;
                    break;
                case 'End':
                    next = items.length - 1;
                    break;
                case 'Tab':
                    // Tab verlaesst das Menue und schliesst es (WAI-ARIA-Menue-Pattern).
                    if (closeOnTab)
                        setOpen(false);
                    return;
                default:
                    return;
            }
            e.preventDefault();
            items[next].focus();
        };
        document.addEventListener('pointerdown', onPointer);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('pointerdown', onPointer);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, rovingFocus, itemSelector, closeOnTab]);
    // Fokus beim Oeffnen in das Menue, beim Schliessen zurueck zum Trigger.
    useEffect(() => {
        if (!rovingFocus)
            return;
        const node = containerRef.current;
        if (open) {
            // Erstes bedienbares Element; ohne Eintraege das Popover selbst
            // (role dialog/menu mit tabindex="-1"), damit Escape/Pfeile greifen.
            const first = Array.from(node?.querySelectorAll(itemSelector) ?? []).find((el) => !el.hasAttribute('disabled') && el.getAttribute('aria-disabled') !== 'true');
            const fallback = node?.querySelector('[role="dialog"][tabindex], [role="menu"][tabindex]');
            (first ?? fallback)?.focus();
        }
        else if (wasOpen.current) {
            const trigger = node?.querySelector('[aria-expanded]');
            if (trigger && (!document.activeElement || document.activeElement === document.body || node?.contains(document.activeElement))) {
                trigger.focus();
            }
        }
        wasOpen.current = open;
    }, [open, rovingFocus, itemSelector]);
    return { open, setOpen, close, toggle, containerRef };
}
